import React from 'react';
import { Plus, X } from 'lucide-react';
import { InlineLoadingState } from '../../../components/LoadingSpinner';
import { SettingsCard } from '../components/SettingsCard';
import { SettingsCardSavedStatus } from '../components/SettingsCardSavedStatus';
import {
  SETTINGS_BTN_OUTLINE,
  SETTINGS_BTN_PRIMARY,
} from '../utils/settingsSectionStyles';
import { IncomingInvoiceMailboxConnectModal } from './IncomingInvoiceMailboxConnectModal';
import type { InboxAccount } from './email/types';

type IncomingInvoiceAccount = {
  id: string;
  email: string;
  is_private: boolean;
  owner_name: string | null;
  can_manage: boolean;
};

const CHECKBOX_CLASS = 'h-4 w-4 rounded border-app-border-strong text-pipedrive-blue focus:ring-pipedrive-blue/30';

export function IncomingInvoiceMailboxSettingsCard() {
  const [accounts, setAccounts] = React.useState<InboxAccount[]>([]);
  const [loaded, setLoaded] = React.useState(false);
  const [savingId, setSavingId] = React.useState<string | null>(null);
  const [saved, setSaved] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [allowImages, setAllowImages] = React.useState(false);
  const [savingImages, setSavingImages] = React.useState(false);

  // Eigene Liste statt `accounts`: enthält auch private Rechnungs-Postfächer anderer Nutzer.
  const [ingestAccounts, setIngestAccounts] = React.useState<IncomingInvoiceAccount[]>([]);

  const loadIngestAccounts = React.useCallback(async () => {
    const r = await fetch('/api/inbox/incoming-invoice-accounts', { credentials: 'include' });
    if (!r.ok) throw new Error((await r.json().catch(() => null))?.error || 'Postfächer konnten nicht geladen werden.');
    const list = (await r.json()) as IncomingInvoiceAccount[];
    setIngestAccounts(Array.isArray(list) ? list : []);
  }, []);

  const loadAccounts = React.useCallback(() => {
    setError(null);
    Promise.all([
      fetch('/api/inbox/accounts', { credentials: 'include' }).then(async (r) => {
        if (!r.ok) throw new Error((await r.json().catch(() => null))?.error || 'Postfächer konnten nicht geladen werden.');
        return r.json() as Promise<InboxAccount[]>;
      }),
      loadIngestAccounts(),
    ])
      .then(([list]) => {
        setAccounts(Array.isArray(list) ? list : []);
        setLoaded(true);
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : 'Laden fehlgeschlagen.');
        setLoaded(true);
      });
  }, [loadIngestAccounts]);

  React.useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  React.useEffect(() => {
    fetch('/api/settings/company', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { incoming_invoice_mail_images?: boolean } | null) => {
        setAllowImages(data?.incoming_invoice_mail_images === true);
      })
      .catch(() => {});
  }, []);

  const saveAllowImages = async (next: boolean) => {
    setSavingImages(true);
    setSaved(false);
    setError(null);
    setAllowImages(next);
    try {
      const res = await fetch('/api/settings/company', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ incoming_invoice_mail_images: next }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((data as { error?: string }).error || 'Speichern fehlgeschlagen.');
      setAllowImages((data as { incoming_invoice_mail_images?: boolean }).incoming_invoice_mail_images ?? next);
      setSaved(true);
    } catch (e) {
      setAllowImages(!next);
      setError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSavingImages(false);
    }
  };

  React.useEffect(() => {
    if (!saved) return undefined;
    const timer = window.setTimeout(() => setSaved(false), 2500);
    return () => window.clearTimeout(timer);
  }, [saved]);

  const patchAccount = async (
    accountId: string,
    patch: { incoming_invoice_ingest?: boolean },
  ) => {
    setSavingId(accountId);
    setSaved(false);
    setError(null);
    try {
      const res = await fetch(`/api/inbox/accounts/${encodeURIComponent(accountId)}/incoming-invoice`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(patch),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((data as { error?: string }).error || 'Speichern fehlgeschlagen.');
      setAccounts((prev) => prev.map((acc) => (String(acc.id) === accountId ? { ...acc, ...data } : acc)));
      await loadIngestAccounts();
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSavingId(null);
    }
  };

  const returnPath = typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : '/settings/offers-invoices';

  return (
    <>
    <SettingsCard
      title="Eingangsrechnungen per E-Mail"
      description={
        allowImages
          ? 'PDF-, XML- und Bild-Anhänge aus dem festgelegten Postfach werden automatisch als Eingangsrechnungen übernommen.'
          : 'PDF- und XML-Anhänge aus dem festgelegten Postfach werden automatisch als Eingangsrechnungen übernommen.'
      }
      actions={
        <>
          <SettingsCardSavedStatus saved={saved} />
          {loaded ? (
            <button type="button" onClick={() => setModalOpen(true)} className={SETTINGS_BTN_PRIMARY}>
              <Plus className="w-4 h-4" />
              {ingestAccounts.length > 0 ? 'Weiteres Postfach verbinden' : 'Postfach verbinden'}
            </button>
          ) : null}
        </>
      }
    >
      {error ? (
        <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">{error}</div>
      ) : null}

      {!loaded ? (
        <InlineLoadingState label="Postfächer werden geladen …" />
      ) : ingestAccounts.length === 0 ? (
        <p className="text-sm text-app-text-subtle">Noch kein Postfach für das Einlesen festgelegt.</p>
      ) : (
        <ul className="space-y-2">
          {ingestAccounts.map((acc) => {
            const id = String(acc.id);
            const busy = savingId === id;
            return (
              <li
                key={id}
                className="flex items-center justify-between gap-3 rounded-lg border border-app-border bg-app-surface px-3 py-2.5"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-app-text">{acc.email}</p>
                  {acc.is_private ? (
                    <p className="truncate text-xs text-app-text-subtle">
                      Privates Postfach{acc.owner_name ? ` von ${acc.owner_name}` : ''}
                    </p>
                  ) : null}
                </div>
                {acc.can_manage ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void patchAccount(id, { incoming_invoice_ingest: false })}
                    className={`${SETTINGS_BTN_OUTLINE} px-2.5`}
                    title="Einlesen beenden"
                    aria-label={`Einlesen für ${acc.email} beenden`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      {loaded ? (
        <label
          htmlFor="incoming-invoice-mail-images"
          className={`mt-4 flex items-start gap-2.5 ${savingImages ? 'cursor-default' : 'cursor-pointer'}`}
        >
          <input
            id="incoming-invoice-mail-images"
            type="checkbox"
            className={`${CHECKBOX_CLASS} mt-0.5`}
            checked={allowImages}
            disabled={savingImages}
            onChange={(e) => void saveAllowImages(e.target.checked)}
          />
          <span className="text-sm text-app-text">
            Auch Bilder (JPG, PNG, HEIC) als Eingangsrechnung übernehmen
            <span className="mt-0.5 block text-xs text-app-text-subtle">
              Für fotografierte Belege. Logos und Signaturbilder werden übersprungen, jedes andere Bild im Anhang wird als Beleg angelegt.
            </span>
          </span>
        </label>
      ) : null}
    </SettingsCard>

      <IncomingInvoiceMailboxConnectModal
        open={modalOpen}
        accounts={accounts}
        returnPath={returnPath}
        onClose={() => setModalOpen(false)}
        onAssigned={(account) => {
          setAccounts((prev) => {
            const id = String(account.id);
            const exists = prev.some((item) => String(item.id) === id);
            return exists
              ? prev.map((item) => (String(item.id) === id ? { ...item, ...account } : item))
              : [...prev, account];
          });
          void loadIngestAccounts().catch(() => {});
          setSaved(true);
        }}
        onError={setError}
      />
    </>
  );
}
